import { Gamepad2, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { trpc } from "../../lib/trpc";
import { Button, Input, Modal, ModalBody, ModalFooter, Textarea } from "../ui";

type Props = {
  clubId: string;
};

export function GamesSection({ clubId }: Props) {
  const utils = trpc.useUtils();
  const { data: games, isLoading } = trpc.clubGame.list.useQuery({ clubId });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGame, setEditingGame] = useState<string | null>(null);

  const deleteMutation = trpc.clubGame.delete.useMutation({
    onSuccess: () => {
      toast.success("Game removed");
      void utils.clubGame.list.invalidate({ clubId });
    },
    onError: () => toast.error("Failed to remove game"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-surface-400 uppercase tracking-wider">Club Games</p>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Plus className="size-3.5" />}
          onClick={() => setShowAddModal(true)}
        >
          Add Game
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : games && games.length > 0 ? (
        <div className="space-y-2">
          {games.map((game) => (
            <div
              key={game.id}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/30 border border-surface-700"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20">
                <Gamepad2 className="size-5 text-brand-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white truncate">{game.name}</p>
                {game.teamSize && (
                  <p className="text-xs text-surface-400">
                    {game.teamSize}v{game.teamSize}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2"
                  onClick={() => setEditingGame(game.id)}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !text-error-400 hover:!bg-error-500/10"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate({ clubGameId: game.id })}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-surface-800/30 border border-surface-700 text-center">
          <Gamepad2 className="size-8 text-surface-500 mb-3" />
          <p className="text-sm text-surface-400">No games configured yet</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            leftIcon={<Plus className="size-3.5" />}
            onClick={() => setShowAddModal(true)}
          >
            Add First Game
          </Button>
        </div>
      )}

      <GameFormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        clubId={clubId}
        mode="create"
      />

      {editingGame && (
        <GameFormModal
          open={true}
          onClose={() => setEditingGame(null)}
          clubId={clubId}
          mode="edit"
          clubGameId={editingGame}
        />
      )}
    </div>
  );
}

function GameFormModal({
  open,
  onClose,
  clubId,
  mode,
  clubGameId,
}: {
  open: boolean;
  onClose: () => void;
  clubId: string;
  mode: "create" | "edit";
  clubGameId?: string;
}) {
  const utils = trpc.useUtils();
  const { data: existingGame } = trpc.clubGame.list.useQuery(
    { clubId },
    { enabled: mode === "edit" && !!clubGameId },
  );
  const game = existingGame?.find((g) => g.id === clubGameId);

  const [name, setName] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [description, setDescription] = useState("");

  // Sync edit data when game loads
  useEffect(() => {
    if (game) {
      setName(game.name);
      setTeamSize(game.teamSize?.toString() ?? "");
      setDescription(game.description ?? "");
    }
  }, [game]);

  const createMutation = trpc.clubGame.create.useMutation({
    onSuccess: () => {
      toast.success("Game added");
      void utils.clubGame.list.invalidate({ clubId });
      onClose();
      setName("");
      setTeamSize("");
      setDescription("");
    },
    onError: (err) => toast.error(err.message || "Failed to add game"),
  });

  const updateMutation = trpc.clubGame.update.useMutation({
    onSuccess: () => {
      toast.success("Game updated");
      void utils.clubGame.list.invalidate({ clubId });
      onClose();
    },
    onError: (err) => toast.error(err.message || "Failed to update game"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const ts = teamSize ? parseInt(teamSize, 10) : undefined;

    if (mode === "edit" && clubGameId) {
      updateMutation.mutate({
        clubGameId,
        name: trimmedName,
        teamSize: ts,
        description: description.trim() || undefined,
      });
    } else {
      createMutation.mutate({
        clubId,
        name: trimmedName,
        teamSize: ts,
        description: description.trim() || undefined,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal open={open} onClose={onClose} className="!max-w-sm">
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-6 pb-1 flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand-500/10 border border-brand-500/20">
            <Gamepad2 className="size-5 text-brand-400" />
          </div>
          <h2 className="text-xl font-bold text-white">
            {mode === "create" ? "Add Game" : "Edit Game"}
          </h2>
        </div>
        <ModalBody className="space-y-4">
          <Input
            label="Game Name"
            placeholder="e.g. Valorant"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
          <Input
            label="Team Size"
            placeholder="e.g. 5"
            type="number"
            value={teamSize}
            onChange={(e) => setTeamSize(e.target.value)}
          />
          <Textarea
            label="Description"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={2}
            resize="none"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!name.trim() || isPending}
            leftIcon={isPending ? <Loader2 className="size-4 animate-spin" /> : undefined}
          >
            {isPending ? "Saving..." : mode === "create" ? "Add Game" : "Save"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
